import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, MenuItem, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { AppBarWithButtons } from '../../../../components/designe/app-bars/app-bar-with-action-buttons/AppBarWithButtons';
import AppEntityAvatar from '../../../../components/designe/app-entity-avatar/app-entity-avatar/AppEntityAvatar';
import AppUploadFileButton from '../../../../components/designe/buttons/app-upload-file-button/AppUploadFileButton';
import AppFormTextField from '../../../../components/designe/forms/app-form-text-field';
import PageContainer from '../../../../components/layout/page-container/PageContainer';
import { useRegisterPageContainerError } from '../../../../components/layout/page-container/page-container-error-handler';
import { useNavigationStack } from '../../../../context/NavigationStackContext';
import withRouter from '../../../../hoc/with-router';
import {
  AccessScope,
  AvatarSize,
  AvatarTypes,
  PostsInteractionScope,
} from '../../../../shared/constants';
import { uploadFilesUtils } from '../../../../utils/files/upload-files-utils';
import { useStyles } from './NewCommunity.styles';
import { useNewCommunityComponent } from './useNewCommunityComponent';

function NewCommunity(props) {
  const { params } = props;
  const {
    formik,
    avatar,
    setAvatar,
    isLoading,
    community,
    communityLoadError,
    isFetchingCommunity,
    retryLoadCommunity,
  } = useNewCommunityComponent(params.communityId);
  const pageDataOwners = useMemo(
    () => ({
      community: community ?? null,
    }),
    [community],
  );
  const handleRetry = useCallback(() => {
    retryLoadCommunity();
  }, [retryLoadCommunity]);
  useRegisterPageContainerError(
    params.communityId
      ? {
          errors: communityLoadError ? [communityLoadError] : [],
          isLoading: isFetchingCommunity && !pageDataOwners.community,
          hasData: Boolean(pageDataOwners.community),
          onRetry: handleRetry,
        }
      : null,
  );
  const { t } = useTranslation();
  const { back } = useNavigationStack();
  const theme = useTheme();
  const { classes } = useStyles(theme);

  return (
    <PageContainer
      title={
        pageDataOwners.community
          ? pageDataOwners.community.title
          : t('communities.newCommunity')
      }
      backButton>
      <div className={classes.newCommunityWrapper}>
        <form
          onSubmit={formik.handleSubmit}
          className={classes.newCommunityForm}>
          <Box className={classes.title}>
            <Typography variant="label-text-sm">
              {t('common.avatar')}
            </Typography>
            <div className={classes.avatarWrapper}>
              <AppEntityAvatar
                fileBlob={avatar?.fileBlob}
                previewStorageLink={
                  pageDataOwners.community?.avatar?.previewStorageLink
                }
                storageLink={pageDataOwners.community?.avatar?.storageLink}
                size={AvatarSize.l}
                type={AvatarTypes.square}
              />
            </div>
          </Box>
          <AppUploadFileButton
            variant="outlined"
            label={t('communities.updateAvatar')}
            onFileInput={(e) => {
              uploadFilesUtils(e.target.files).then((uploadedFiles) => {
                setAvatar(uploadedFiles[0]);
              });
            }}
          />
          <AppFormTextField
            label={t('common.title')}
            fullWidth
            autoFocus
            name="title"
            formik={formik}
          />
          <AppFormTextField
            label={t('common.description')}
            fullWidth
            multiline
            minRows={3}
            maxRows={15}
            name="description"
            formik={formik}
          />
          <AppFormTextField
            label={t('communities.pagePrivacy')}
            fullWidth
            name="scope"
            formik={formik}
            select>
            {Object.values(AccessScope).map((scope) => (
              <MenuItem key={scope} value={scope}>
                {t(`communities.communityPrivacy_${scope}`)}
              </MenuItem>
            ))}
          </AppFormTextField>
          <AppFormTextField
            label={t('communities.whoCanAddPost')}
            fullWidth
            name="postsScope"
            formik={formik}
            select>
            {Object.values(PostsInteractionScope).map((scope) => (
              <MenuItem key={scope} value={scope}>
                {t(`communities.postPrivacy_${scope}`)}
              </MenuItem>
            ))}
          </AppFormTextField>
          <AppFormTextField
            label={t('communities.whoCanComment')}
            fullWidth
            name="commentsScope"
            formik={formik}
            select>
            {Object.values(PostsInteractionScope).map((scope) => (
              <MenuItem key={scope} value={scope}>
                {t(`communities.commentPrivacy_${scope}`)}
              </MenuItem>
            ))}
          </AppFormTextField>
          <div className={classes.buttonWrapper}>
            <AppBarWithButtons
              loading={isLoading}
              onCancelClick={() => back()}
            />
          </div>
        </form>
      </div>
    </PageContainer>
  );
}

export default withRouter(NewCommunity);
